import { Button } from '@/components/ui/button';
import * as Field from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toaster } from '@/components/ui/toast';
import { DataCombobox } from '@components/DataCombobox';
import { createForm, setValue, valiForm } from '@modular-forms/solid';
import { userId } from '@state/auth';
import { useCalendarState } from '@state/events/calendar-state';
import { useMutation } from '@tanstack/solid-query';
import { useService } from 'solid-services';
import { queryClient } from 'src/App';
import { grid } from 'styled-system/patterns';
import { fetchEventGroups, updateEvent } from './api';
import { type EventForm, EventSchema } from './eventSchema';

export default function () {
  // Pulling directly from calendar state
  const { activeEvent, setActiveEvent } = useService(useCalendarState)();

  // 1. Setup Mutation (Update Only)
  const mutation = useMutation(() => ({
    mutationFn: (values: EventForm) => {
      const existing = activeEvent();
      if (!existing?.id) {
        throw new Error('No active event selected to update.');
      }
      return updateEvent({ id: existing.id, values });
    },
    onSuccess: () => {
      toaster.create({
        title: 'Event updated',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['events', userId()] });

      if (setActiveEvent) {
        setActiveEvent(undefined);
      }
    },
    onError: (error) => {
      toaster.create({
        title: 'Error updating event',
        description: error.message,
        type: 'error',
      });
    },
  }));

  // 2. Setup Form
  const [eventForm, { Form, Field: FormField }] = createForm<EventForm>({
    validate: valiForm(EventSchema),
    initialValues: {
      description: activeEvent()?.description ?? '',
      // Format timestamp for datetime-local: YYYY-MM-DDTHH:mm
      starts_at: activeEvent()?.starts_at
        ? new Date(activeEvent()!.starts_at).toISOString().slice(0, 16)
        : new Date().toISOString().slice(0, 16),
      duration_mins: activeEvent()?.duration_mins ?? 60,
      rrule: activeEvent()?.rrule ?? '',
      meeting_url: activeEvent()?.meeting_url ?? '',
      group: activeEvent()?.group ?? '',
    }
  });

  const handleSubmit = (values: EventForm) => {
    mutation.mutate(values);
  };

  return (
    <Form onSubmit={handleSubmit} class={grid({ gap: '3', w: 'full' })}>

      {/* Starts At (Date & Time) */}
      <FormField name="starts_at">
        {(field, props) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Start Time (UTC)</Field.Label>
            <Input
              {...props}
              type="datetime-local"
              value={field.value ?? ''}
            />
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      {/* Duration */}
      <FormField name="duration_mins" type="number">
        {(field, props) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Duration (Minutes)</Field.Label>
            <Input
              {...props}
              type="number"
              min="15"
              step={15}
              value={field.value ?? 60}
            />
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      {/* Meeting URL */}
      <FormField name="meeting_url">
        {(field, props) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Meeting URL</Field.Label>
            <Input
              {...props}
              type="url"
              placeholder="https://zoom.us/j/..."
              value={field.value ?? ''}
            />
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      {/* RRule (Recurrence) */}
      <FormField name="rrule">
        {(field, props) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Recurrence Rule (RRULE)</Field.Label>
            <Input
              {...props}
              type="text"
              placeholder="FREQ=WEEKLY;BYDAY=MO,WE,FR"
              value={field.value ?? ''}
            />
            <Field.HelperText>Standard iCalendar RRULE string.</Field.HelperText>
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      {/* Description */}
      <FormField name="description">
        {(field, props) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Description</Field.Label>
            <Textarea
              {...props}
              placeholder="Event details..."
              value={field.value ?? ''}
            />
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      {/* Group Combobox */}
      <FormField name="group">
        {(field) => (
          <Field.Root invalid={!!field.error}>
            <DataCombobox
              label='Group'
              placeholder='Search groups or type a new one...'
              queryKey='event-groups'
              fetchFn={fetchEventGroups}
              value={field.value}
              onValueChange={(val) => {
                setValue(eventForm, 'group', val)
              }}
              error={field.error}
              allowCustomValue={true}
            />
            <Field.HelperText>
              Categorize this event (e.g., 'Weekly Standup').
            </Field.HelperText>
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      <Button
        type="submit"
        loading={mutation.isPending}
        colorPalette="jade"
        variant="solid"
        justifySelf="start"
      >
        Update Event
      </Button>
    </Form>
  );
}
